import { toast } from 'sonner';

import { Link } from '@/components/Link';
import { useAxios } from '@/hooks/useAxios';
import { useMy } from '@/hooks/useMy';
import { getQueryKey } from '@/hooks/useQueryKeys';
import { queryClient } from '@/queryclient';
import { UseExtendQueryOptions, useMutation, useQuery } from '@/utils/api';
import { copyToClipboard } from '@/utils/copyToClipboard.util';

export interface PlextracDefinition {
  id: string;
  create: boolean;
}

type AxiosErrorLike = {
  response?: {
    data?: unknown;
    status?: number;
  };
};

const getAxiosErrorDetails = (err: unknown) => {
  if (err && typeof err === 'object' && 'response' in err) {
    const response = (err as AxiosErrorLike).response;
    return {
      data: response?.data,
      status: response?.status,
    };
  }
  return {
    data: undefined,
    status: undefined,
  };
};

interface PlextracResponse {
  username: string;
  key: string;
  id: string;
  kev: boolean;
  exploit: boolean;
  title: string;
  writeupId: number;
}

interface PlexTracClient {
  id: string;
  name: string;
  description: string;
}

interface PlexTracReport {
  id: string;
  name: string;
}

interface ReportingData {
  client: PlexTracClient;
  reports: PlexTracReport[];
}

export function getPlextracWriteupUrl(writeupId: number): string {
  return `https://praetorian.plextrac.com/writeups/${writeupId}/edit`;
}

export function useSyncPlextrac(vulnerabilityName: string, riskKey: string) {
  const axios = useAxios();

  // Query for existing definition (uses vulnerability name)
  const { data: existingDefinition } = useMy(
    {
      resource: 'file',
      query: `definitions/${vulnerabilityName}`,
      global: true,
    },
    {
      enabled: !!vulnerabilityName,
    }
  );

  // Query for existing writeup (uses vulnerability name)
  const { data: existingWriteup } = useMy(
    {
      resource: 'vulnerability',
      query: {
        node: {
          labels: ['Vulnerability'],
          filters: [
            {
              field: 'key',
              operator: '=',
              value: `#vulnerability#${vulnerabilityName}`,
            },
          ],
        },
      },
    },
    {
      enabled: !!vulnerabilityName,
    }
  );

  const mutation = useMutation({
    defaultErrorMessage: 'Failed to sync with PlexTrac',
    mutationFn: async (definition: PlextracDefinition) => {
      // Use riskKey instead of definition.id for the API request
      const promise = axios.put<PlextracResponse>('/plextrac/definition', {
        ...definition,
        key: riskKey,
      });

      toast.promise(promise, {
        loading: 'Syncing PlexTrac Definition...',
        success: 'Successfully synced with PlexTrac',
        error: (err: unknown) => {
          const { data } = getAxiosErrorDetails(err);
          return `Failed to sync with PlexTrac Definition${typeof data === 'string' ? `: ${data}` : ''}`;
        },
      });

      return promise;
    },
    onSuccess: (_response, _variables) => {
      queryClient.invalidateQueries({
        queryKey: getQueryKey.getMy('vulnerability'),
      });

      queryClient.invalidateQueries({
        queryKey: getQueryKey.getMy('risk'),
      });

      // Invalidate all global file queries to ensure we get fresh data
      queryClient.invalidateQueries({
        queryKey: ['my', 'file', { global: true }],
      });
      // Invalidate the vulnerability query to refresh the writeup ID (uses riskKey)
      queryClient.invalidateQueries({
        queryKey: ['my', 'risk', { key: riskKey, global: true }],
      });
    },
    onError: (_error, _variables) => {
      queryClient.invalidateQueries({
        queryKey: getQueryKey.getMy('vulnerability'),
      });

      queryClient.invalidateQueries({
        queryKey: getQueryKey.getMy('risk'),
      });

      // Invalidate all global file queries to ensure we get fresh data
      queryClient.invalidateQueries({
        queryKey: ['my', 'file', { global: true }],
      });
      // Invalidate the vulnerability query to refresh the writeup ID (uses riskKey)
      queryClient.invalidateQueries({
        queryKey: ['my', 'risk', { key: riskKey, global: true }],
      });
    },
  });

  const handleViewInPlextrac = (writeupId: number) => {
    window.open(
      getPlextracWriteupUrl(writeupId),
      '_blank',
      'noopener noreferrer'
    );
  };

  // Access the first vulnerability from the response
  const vulnerability = existingWriteup?.[0];

  return {
    ...mutation,
    existingDefinition,
    existingWriteup: vulnerability,
    hasExistingWriteup: !!vulnerability?.writeupId,
    hasExistingDefinition: !!existingDefinition,
    handleViewInPlextrac,
  };
}

export const useGetPlextracReports = ({
  enabled,
  customerEmail,
}: {
  enabled?: boolean;
  customerEmail?: string;
}) => {
  const axios = useAxios({ email: customerEmail });

  return useQuery<ReportingData>({
    defaultErrorMessage: 'Failed to fetch PlexTrac reports',
    queryKey: getQueryKey.getPlextracReports(customerEmail),
    queryFn: async () => {
      const { data } = await axios.get('/plextrac/reporting');
      return data;
    },
    enabled,
  });
};

export const useForcePlexTracSync = (customerEmail?: string) => {
  const axios = useAxios({ email: customerEmail });

  return useMutation<void, Error, void>({
    defaultErrorMessage: 'Failed to force sync PlexTrac findings',
    mutationFn: async () => {
      const promise = axios.post('/functions/findings', { force: true });

      toast.promise(promise, {
        loading: 'Force-syncing PlexTrac findings...',
        success:
          'PlexTrac sync started. Please allow up to 5 minutes for your findings to sync.',
        error: (err: unknown) => {
          const { data } = getAxiosErrorDetails(err);
          if (typeof data === 'string') {
            return data;
          }
          return 'Failed to force sync PlexTrac findings';
        },
      });

      await promise;
    },
  });
};

export function usePushToPlexTrac(customerEmail?: string) {
  const axios = useAxios({ email: customerEmail });

  return useMutation<
    { findingUrl: string },
    Error,
    { riskKey: string; reportID: number }
  >({
    defaultErrorMessage: 'Failed to push report to PlexTrac',
    mutationFn: async ({
      riskKey,
      reportID,
    }: {
      riskKey: string;
      reportID: number;
    }) => {
      const promise = axios.post('/plextrac/reporting', {
        riskKey,
        reportID,
      });
      toast.promise(promise, {
        loading: 'Pushing finding to PlexTrac report...',
        error: (err: unknown) => {
          const { status } = getAxiosErrorDetails(err);
          if (status === 409) {
            return 'Finding with that title already exists in the report';
          }
          if (status === 504) {
            return 'PlexTrac API timed out';
          }
          return 'Failed to push finding to PlexTrac report';
        },
      });

      const response = await promise;
      return response.data;
    },
    onSuccess: response => {
      copyToClipboard(response.findingUrl);
      toast.success('Finding pushed to PlexTrac report', {
        description: (
          <div>
            <span>View it here: </span>
            <Link
              to={response.findingUrl}
              newTab
              styleType="textPrimary"
              buttonClass="p-0 text-xs text-left"
            >
              {response.findingUrl}
            </Link>
          </div>
        ),
      });
    },
  });
}

export const useGetCurrentPlexTracClient = (
  options?: UseExtendQueryOptions<string> & { customerEmail?: string }
) => {
  // Use customer context for PlexTrac client ID - each customer has their own PlexTrac client
  const axios = useAxios({ email: options?.customerEmail });

  return useQuery<string>({
    ...options,
    defaultErrorMessage: 'Failed to fetch PlexTrac client',
    queryKey: getQueryKey.getPlexTracClientId(options?.customerEmail),
    queryFn: async () => {
      const { data } = await axios.get('/my', {
        params: {
          key: '#configuration#plextrac#client-id',
        },
      });
      return data?.configurations?.[0]?.value || '';
    },
  });
};

export const useValidatePlexTracClient = (customerEmail?: string) => {
  // Use customer context for PlexTrac operations - each customer has their own PlexTrac client
  const axios = useAxios({ email: customerEmail });
  const { invalidate: invalidatePlexTracClient } = useGetCurrentPlexTracClient({
    enabled: false,
    customerEmail,
  });

  return useMutation<
    boolean,
    Error,
    { clientId: string; currentClientId?: string }
  >({
    defaultErrorMessage: 'Failed to validate PlexTrac Client ID',
    mutationFn: async ({ clientId, currentClientId }) => {
      try {
        // First save the new client ID
        await axios.post('/association', {
          namespace: 'plextrac',
          type: 'client-id',
          value: clientId.trim(),
        });

        // Try to fetch reports to validate the client ID actually works
        try {
          await axios.get('/plextrac/reporting', {
            params: {
              published: true,
            },
          });

          // If we successfully got reports, the client ID is valid
          // Invalidate both client ID and reporting queries to clear all caches
          invalidatePlexTracClient();

          // Invalidate old client ID's reporting cache if it existed
          if (currentClientId) {
            queryClient.invalidateQueries({
              queryKey: getQueryKey.getReporting(
                currentClientId,
                customerEmail
              ),
            });
          }

          // Invalidate new client ID's reporting cache to force fresh data
          queryClient.invalidateQueries({
            queryKey: getQueryKey.getReporting(clientId.trim(), customerEmail),
          });

          return true;
        } catch (reportError) {
          // If fetching reports fails, the client ID is invalid
          // Revert to the previous client ID if we had one, or clear if new connection
          if (currentClientId) {
            await axios.post('/association', {
              namespace: 'plextrac',
              type: 'client-id',
              value: currentClientId.trim(),
            });
          } else {
            // No previous ID, so clear the invalid one we just saved
            await axios.delete('/association', {
              data: {
                namespace: 'plextrac',
                type: 'client-id',
              },
            });
          }

          // Invalidate to ensure UI reflects the reverted/cleared state
          invalidatePlexTracClient();

          // Invalidate both old and new client reporting caches to ensure clean state
          if (currentClientId) {
            queryClient.invalidateQueries({
              queryKey: getQueryKey.getReporting(
                currentClientId,
                customerEmail
              ),
            });
          }
          queryClient.invalidateQueries({
            queryKey: getQueryKey.getReporting(clientId.trim(), customerEmail),
          });

          const { status: statusCode, data: errorData } =
            getAxiosErrorDetails(reportError);

          // Check for specific PlexTrac error messages
          if (statusCode === 500 && typeof errorData === 'string') {
            if (errorData.includes('PlexTrac client ID not set')) {
              throw new Error('PlexTrac Client ID is required');
            } else if (
              errorData.includes('PlexTrac client ID is not a number')
            ) {
              throw new Error('PlexTrac Client ID must be a numerical value');
            } else if (
              errorData.includes('Failed to get PlexTrac client details')
            ) {
              throw new Error('Client ID not found in PlexTrac');
            } else if (
              errorData.includes('Failed to authenticate with PlexTrac')
            ) {
              throw new Error(
                'PlexTrac authentication failed - please contact support'
              );
            }
          }

          throw new Error('Unable to validate PlexTrac Client ID');
        }
      } catch (error) {
        // If we couldn't even save the association, there's a different problem
        if (
          (error as Error).message.includes('PlexTrac') ||
          (error as Error).message.includes('Client ID')
        ) {
          throw error; // Re-throw validation errors
        }
        throw new Error('Failed to validate PlexTrac Client ID');
      }
    },
  });
};

export const useUpdatePlexTracClient = (customerEmail?: string) => {
  // Use customer context for PlexTrac operations - each customer has their own PlexTrac client
  const axios = useAxios({ email: customerEmail });
  const { invalidate: invalidatePlexTracClient, data: currentClientId } =
    useGetCurrentPlexTracClient({
      enabled: false,
      customerEmail,
    });

  return useMutation<void, Error, string | null>({
    defaultErrorMessage: 'Failed to update PlexTrac client',
    mutationFn: async (clientId: string | null) => {
      if (
        clientId === null ||
        clientId === '' ||
        (typeof clientId === 'string' && clientId.trim() === '')
      ) {
        // Use DELETE method to disconnect PlexTrac
        await axios.delete('/association', {
          data: {
            namespace: 'plextrac',
            type: 'client-id',
          },
        });
      } else {
        // Normal update with non-empty value
        await axios.post('/association', {
          namespace: 'plextrac',
          type: 'client-id',
          value: clientId.trim(),
        });
      }

      // Invalidate client ID query
      invalidatePlexTracClient();

      // Invalidate reporting cache for the old client ID
      if (currentClientId) {
        queryClient.invalidateQueries({
          queryKey: getQueryKey.getReporting(currentClientId, customerEmail),
        });
      }

      // Invalidate reporting cache for the new client ID (if setting a new one)
      if (clientId && clientId.trim() !== '') {
        queryClient.invalidateQueries({
          queryKey: getQueryKey.getReporting(clientId.trim(), customerEmail),
        });
      }

      return;
    },
  });
};

export const useCreatePlexTracClient = (customerEmail?: string) => {
  // Use customer context for PlexTrac operations - each customer has their own PlexTrac client
  const axios = useAxios({ email: customerEmail });
  const { invalidate: invalidatePlexTracClient } = useGetCurrentPlexTracClient({
    enabled: false,
    customerEmail,
  });

  return useMutation<
    { client_id: number },
    Error,
    { name: string; description?: string }
  >({
    defaultErrorMessage: 'Failed to create PlexTrac client',
    mutationFn: async ({ name, description }) => {
      const promise = axios.post('/plextrac/createClient', {
        name,
        description:
          description || `Auto-generated PlexTrac client for ${name}`,
      });

      toast.promise(promise, {
        loading: 'Creating PlexTrac client...',
        success: 'PlexTrac client created and connected successfully',
        error: (err: unknown) => {
          const { data } = getAxiosErrorDetails(err);
          if (typeof data === 'string') {
            return data;
          }
          return 'Failed to create PlexTrac client';
        },
      });

      const response = await promise;
      return response.data;
    },
    onSuccess: () => {
      // Invalidate client ID query to refresh connection status
      invalidatePlexTracClient();

      // Invalidate reporting queries to clear any cached data
      queryClient.invalidateQueries({
        queryKey: getQueryKey.getReporting(undefined, customerEmail),
      });
    },
  });
};

export const usePlexTracClient = (customerEmail?: string) => {
  const { data: clientId, status: clientIdStatus } =
    useGetCurrentPlexTracClient({
      enabled: customerEmail !== undefined, // Only fetch if customer exists
      customerEmail,
    });

  const {
    mutateAsync: updatePlexTracClient,
    status: updatePlexTracClientStatus,
  } = useUpdatePlexTracClient(customerEmail);

  return {
    clientId,
    clientIdStatus,
    updatePlexTracClient,
    updatePlexTracClientStatus,
  };
};

export const useConnectPlexTracReport = (customerEmail?: string) => {
  const axios = useAxios({ email: customerEmail });

  return useMutation<
    { message: string; report: PlextracReportInfo },
    Error,
    { reportId: number }
  >({
    defaultErrorMessage: 'Failed to connect PlexTrac report',
    mutationFn: async ({ reportId }) => {
      const promise = axios.put('/plextrac/reports/connect', {
        report_id: reportId,
      });

      toast.promise(promise, {
        loading: 'Connecting report to Chariot...',
        success: 'Report connected successfully',
        error: (err: unknown) => {
          const { data } = getAxiosErrorDetails(err);
          if (typeof data === 'string') {
            return data;
          }
          return 'Failed to connect report';
        },
      });

      const response = await promise;
      return response.data;
    },
    onSuccess: () => {
      // Invalidate configurations query to refresh connected reports list
      queryClient.invalidateQueries({
        queryKey: getQueryKey.getMy('configuration'),
      });
    },
  });
};

export const useDisconnectPlexTracReport = (customerEmail?: string) => {
  const axios = useAxios({ email: customerEmail });

  return useMutation<{ message: string }, Error, { reportId: number }>({
    defaultErrorMessage: 'Failed to disconnect PlexTrac report',
    mutationFn: async ({ reportId }) => {
      const promise = axios.delete('/plextrac/reports/connect', {
        data: {
          report_id: reportId,
        },
      });

      toast.promise(promise, {
        loading: 'Disconnecting report from Chariot...',
        success: 'Report disconnected successfully',
        error: (err: unknown) => {
          const { data } = getAxiosErrorDetails(err);
          if (typeof data === 'string') {
            return data;
          }
          return 'Failed to disconnect report';
        },
      });

      const response = await promise;
      return response.data;
    },
    onSuccess: () => {
      // Invalidate configurations query to refresh connected reports list
      queryClient.invalidateQueries({
        queryKey: getQueryKey.getMy('configuration'),
      });
    },
  });
};

export interface PlextracReportInfo {
  id: number;
  name: string;
  client_id: number;
  report_id: number;
}
